import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Badges from './Badges';

const ProfileBadges = ({ progress,ShowViewMore}) => {
  const [expanded, setExpanded] = useState(false);
  const [hoveredBadge, setHoveredBadge] = useState(null);

  const sortedBadges = [...Badges].reverse();
  
  const currentTierIndex = sortedBadges.findIndex(
    (tier) => progress >= tier.range[0] && progress <= tier.range[1]
  );
  
  const currentBadge = currentTierIndex !== -1 ? sortedBadges[currentTierIndex] : null;
  const previousBadges = sortedBadges.slice(0, currentTierIndex);
  const futureBadges = sortedBadges.slice(currentTierIndex + 1);

  const BadgeCard = ({ badge, isCurrent, isLocked }) => (
    <div
      className={`
        relative flex flex-col items-center p-4 rounded-xl
        transition-all duration-300 ease-in-out
        ${isLocked ? 'opacity-50' : ''}
        ${hoveredBadge === badge.name ? 'bg-blue-50 shadow-lg scale-105' : 'hover:bg-gray-50 hover:scale-102'}
        ${isCurrent ? 'bg-gradient-to-b from-white to-blue-50' : ''}
      `}
      onMouseEnter={() => setHoveredBadge(badge.name)}
      onMouseLeave={() => setHoveredBadge(null)}
    >
      <div className={`
        relative w-20 h-20 mb-2 group
        ${isCurrent ? 'animate-subtle-float' : ''}
      `}>
        {/* Animated glow effect for current badge */}
        {isCurrent && (
          <div className="absolute inset-0 bg-blue-400 rounded-full blur-lg opacity-20 animate-pulse-slow" />
        )}
        
        <img 
          src={badge.icon} 
          alt={badge.name} 
          className={`
            relative w-full h-full object-contain rounded-full
            transition-all duration-500 ease-in-out
            ${isCurrent ? 'ring-4 ring-blue-400 ring-opacity-40 shadow-lg' : ''}
            group-hover:transform group-hover:scale-105
          `}
        />
      </div>
      
      <p className={`
        text-sm font-medium transition-colors duration-300
        ${isCurrent ? 'text-blue-600' : 'text-gray-700'}
      `}>
        {badge.name}
      </p>
      
      {isCurrent ? (
        <div className="flex flex-col items-center space-y-1 mt-1">
          <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 rounded-full animate-progress-pulse"
              style={{
                width: `${(progress - badge.range[0]) / (badge.range[1] - badge.range[0]) * 100}%`
              }}
            />
          </div>
          <div className="
            mt-2 px-3 py-1 
            bg-gradient-to-r from-blue-500 to-blue-600
            text-white text-xs rounded-full
            shadow-md transform
            animate-badge-indicator
          ">
            Current
          </div>
        </div>
      ) : (
        <span className="text-xs text-gray-500 mt-1 text-center">
          {isLocked ? `Unlock at ${badge.range[0]}` : `Earned ${badge.range[1]} coins`}
        </span>
      )}

      {/* Tooltip */}
      {hoveredBadge === badge.name && (
        <div className="
          absolute -bottom-2 left-1/2 transform -translate-x-1/2
          bg-gray-800 text-white text-xs px-3 py-1.5 rounded-lg
          shadow-lg z-10 whitespace-nowrap
          animate-fade-up
        ">
          Range: {badge.range[0]}-{badge.range[1]}
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full p-6 pt-2 bg-white rounded-xl shadow-sm">
      <div className="flex justify-between items-center ">
        <h3 className="text-xl font-bold text-gray-800">Badges</h3>
        {ShowViewMore && <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800">
          {expanded ? (
            <>
              View Less <ChevronUp className="w-4 h-4 ml-1 transition-transform duration-200" />
            </>
          ) : (
            <>
              View More <ChevronDown className="w-4 h-4 ml-1 transition-transform duration-200" />
            </>
          )}
        </button>}
      </div>

      <div className={`
        grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-4
        transition-all duration-500 ease-in-out
      `}>
        {previousBadges.map((badge) => (
          <BadgeCard key={badge.name} badge={badge} />
        ))}
        {currentBadge && <BadgeCard badge={currentBadge} isCurrent={true} />}
        {expanded && futureBadges.map((badge) => (
          <BadgeCard key={badge.name} badge={badge} isLocked={true} />
        ))}
      </div>
    </div>
  );
};

// Add these styles to your global CSS or Tailwind config
const style = document.createElement('style');
style.textContent = `
  @keyframes subtle-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  }
  
  @keyframes pulse-slow {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.3; }
  }
  
  @keyframes progress-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
  
  @keyframes badge-indicator {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  @keyframes fade-up {
    from { 
      opacity: 0;
      transform: translate(-50%, 5px);
    }
    to { 
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
  
  .animate-subtle-float {
    animation: subtle-float 4s ease-in-out infinite;
  }
  
  .animate-pulse-slow {
    animation: pulse-slow 3s ease-in-out infinite;
  }
  
  .animate-progress-pulse {
    animation: progress-pulse 2s ease-in-out infinite;
  }
  
  .animate-badge-indicator {
    animation: badge-indicator 2s ease-in-out infinite;
  }
  
  .animate-fade-up {
    animation: fade-up 0.2s forwards;
  }
  
  .hover\:scale-102:hover {
    transform: scale(1.02);
  }
`;
document.head.appendChild(style);

export default ProfileBadges;